import React, { Fragment } from 'react';
import { injectIntl } from 'react-intl';

import { connect, useDispatch } from 'react-redux';

import { Feedback, Flexer, Link, Spacer } from '@ontruck/orc';

import QuantityBox from 'modules/Common/QuantityBox';

import Actions from './Actions';

import OrderStatus from 'modules/Common/Detail/StatusInfo/OrderStatus';

import {
  CANCELLED,
  PENDING_ASSIGNMENT,
  PENDING_CONFIRMATION,
  PENDING_MODERATION,
  PENDING_PAYMENT,
} from 'modules/shipper-order/Form/constants';

import {
  copyOrderDetails,
  editOrder,
  viewValidVehicles,
} from 'redux/modules/shipper-order-detail/effects';

import { openJourneyDetail } from 'redux/modules/journeys/effects';

import {
  getCostQuote,
  getShipperOrderDetailData,
} from 'redux/modules/shipper-order-detail/selectors';

import { HighPrioCheckbox } from 'modules/high-prio-order';
import ServiceTag, { SKINS } from '../../../../../components/ServiceTag';

const t = {
  shipper_orders: '{number, plural, one {Shipper Order} other {Shipper Orders}}',
  stop_numbers: '{number, plural, one {{number} stop} other {{number} stops}}',
  history_title: 'History',
  message_launched: 'This order has been launched to the pool as a job.',
  message_assigned: 'This order has been assigned to a driver as a job.',
  message_confirmation_payment:
    'There has been a problem with the payment of this order. Please contact us.',
  message_fleet: 'This order should be managed from the Fleet tab',
  go_to_job: 'Go to job',
  jobs: 'Jobs:',
  nonCore: 'RFQ',
};

const LinksToJourneys = ({ journeys }) => {
  const dispatch = useDispatch();

  return (
    <Spacer mTop="XS">
      <div>{t.jobs}</div>
      {journeys.map((journey) => (
        <div key={journey.id}>
          <Link
            skin="dark"
            weight="bold"
            text={`J${journey.id}`}
            onClick={() => {
              dispatch(openJourneyDetail({ id: journey.id }));
            }}
            dataE2E="detail__goToJob"
          />
        </div>
      ))}
    </Spacer>
  );
};

const StatusInfo = ({ shipperOrder, copyOrderDetails, quote, fetching, viewValidVehicles }) => {
  if (fetching || !shipperOrder) {
    return null;
  }

  return (
    <Fragment>
      <Flexer.Parent alignItems="flex-start" fullWidth>
        <QuantityBox
          mRight="M"
          quantityType="cost"
          allowEdit={false}
          loading={quote.loading}
          currency={shipperOrder.currency}
          {...quote}
        />
        <Flexer.Child flex="1">
          <Flexer.Parent mBottom="S">
            <HighPrioCheckbox
              shipperOrderId={shipperOrder?.id}
              isHighPriority={shipperOrder?.is_high_priority}
            />
            {shipperOrder?.is_core === false && (
              <Spacer mLeft="XS">
                <ServiceTag text={t.nonCore} skin={SKINS.deepPurple} textTransform="uppercase" />
              </Spacer>
            )}
          </Flexer.Parent>
          {shipperOrder.status === PENDING_MODERATION && (
            <Actions
              item={shipperOrder}
              copyDetail={copyOrderDetails}
              viewValidVehicles={viewValidVehicles}
            />
          )}

          {[CANCELLED].includes(shipperOrder.status) && (
            <Spacer mBottom="XS">
              <OrderStatus status={shipperOrder.status} />
            </Spacer>
          )}

          {[PENDING_CONFIRMATION, PENDING_PAYMENT].includes(shipperOrder.status) && (
            <Feedback size="wideS" skin="warning">
              {t.message_confirmation_payment}
            </Feedback>
          )}

          {![PENDING_CONFIRMATION, PENDING_PAYMENT, PENDING_MODERATION].includes(
            shipperOrder.status
          ) && (
            <Fragment>
              {(() => {
                if (!(shipperOrder.journeys || []).length && shipperOrder.status !== CANCELLED) {
                  return <div>{t.message_fleet}</div>;
                }

                if (shipperOrder.status === PENDING_ASSIGNMENT) {
                  return (
                    <>
                      <Feedback size="wideS" skin="important">
                        {t.message_launched}
                      </Feedback>
                      <LinksToJourneys journeys={shipperOrder.journeys} />
                    </>
                  );
                }

                if (!(shipperOrder.journeys || []).length) {
                  return null;
                }

                return (
                  <>
                    <Feedback size="wideS" skin="important">
                      {t.message_assigned}
                    </Feedback>
                    <LinksToJourneys journeys={shipperOrder.journeys} />
                  </>
                );
              })()}
            </Fragment>
          )}
        </Flexer.Child>
      </Flexer.Parent>
    </Fragment>
  );
};

const actionCreators = {
  copyOrderDetails,
  editOrder,
  viewValidVehicles,
};

export default injectIntl(
  connect(
    (state, props) => ({
      shipperOrder: getShipperOrderDetailData(state),
      quote: getCostQuote(state),
    }),
    actionCreators
  )(StatusInfo)
);
